import type { PaymentChannel } from '@festina/moneyout-general-types/moneyout'
import { useAuthenticationContext } from '@festina-life/authentication'
import { notEmpty } from '@festina-life/core'
import { useQuery } from '@tanstack/react-query'
import { useCallback } from 'react'

import { useHook } from '../lenteHooks'
import { useAvailableComponents } from './useAvailableComponents'

export const useLazyFetchPaymentChannelBankAccount = (
  paymentChannel: PaymentChannel,
) => {
  const { pseudonymization } = useAuthenticationContext()
  const { isUsingLegalEntity } = useAvailableComponents()
  const tenantId = useHook('routing.tenant.useTenantParam')()
  const pseudonymizablePaymentChannelFields = useHook(
    'pages.paymentChannels.usePseudonymizablePaymentChannelFields',
  )(paymentChannel)
  const { mutateAsync } = useHook('lazyFetchers.crm.useLazyFetchPiiEntries')()

  const isPaymentChannelPseudonymized =
    !!pseudonymization?.moneyOut?.paymentChannel

  const fetchDepseudonymizedBankAccountData = useCallback(async () => {
    const depseudonymizedAccount = await Promise.all(
      Object.entries(pseudonymizablePaymentChannelFields).map(
        async ([field, id]) => {
          if (!id || typeof id !== 'string') {
            return undefined
          }
          const result = await mutateAsync({
            xTenantId: tenantId,
            id,
          })

          return [field, result] as const
        },
      ),
    )
    const paymentChannelAccount = Object.fromEntries(
      depseudonymizedAccount.filter(notEmpty),
    )

    return {
      ...paymentChannel,
      ...paymentChannelAccount,
    } satisfies PaymentChannel
  }, [
    mutateAsync,
    paymentChannel,
    tenantId,
    pseudonymizablePaymentChannelFields,
  ])

  const {
    data: resolvedPaymentChannel,
    isLoading,
    error,
  } = useQuery({
    queryKey: [
      'fetchPiiEntries',
      tenantId,
      paymentChannel?._type,
      pseudonymizablePaymentChannelFields,
    ],
    queryFn: () => fetchDepseudonymizedBankAccountData(),
    enabled: isPaymentChannelPseudonymized && isUsingLegalEntity,
  })

  return {
    resolvedPaymentChannel:
      isPaymentChannelPseudonymized && isUsingLegalEntity
        ? resolvedPaymentChannel
        : paymentChannel,
    isLoading,
    error,
  }
}
